<script setup>
import { computed, onMounted } from "vue";

const props = defineProps({
  title: {
    type: String,
    required: true,
  },
  date: {
    type: String,
    default: "",
  },
  description: {
    type: String,
    default: "",
  },
  icon: {
    type: [String, Object],
    component: {
      type: String,
    },
    color: {
      type: String,
    },
    default: () => ({
      color: "success",
    }),
  },
  color: {
    type: String,
    default: "success",
  },
  closeHandler: {
    type: Function,
    default: null,
  },
});

onMounted(() => {
  setTimeout(() => {
    props.closeHandler?.();
  }, 4000);
});

const iconClass = computed(() => {
  if (props.icon && typeof props.icon === "string") return props.icon;
  if (props.icon && typeof props.icon === "object") return props.icon.component || "";
  return "";
});

const semanticColor = computed(() => {
  if (props.color && props.color !== "white") return props.color;
  if (props.icon && typeof props.icon === "object" && props.icon.color) {
    return props.icon.color;
  }
  return props.color || "success";
});

const toneClass = computed(() => {
  if (semanticColor.value === "danger") return "is-danger";
  return "is-success";
});
</script>

<template>
  <div class="argon-snackbar toast fade show" :class="toneClass">
    <div class="argon-snackbar__accent" aria-hidden="true"></div>
    <div class="argon-snackbar__icon-wrap">
      <i v-if="iconClass" class="argon-snackbar__icon" :class="iconClass" />
    </div>
    <div class="argon-snackbar__body">
      <div class="argon-snackbar__topline">
        <span class="argon-snackbar__title">{{ title }}</span>
        <small v-if="date" class="argon-snackbar__date">{{ date }}</small>
      </div>
      <div
        v-if="description"
        class="argon-snackbar__description"
        v-html="description"
      ></div>
    </div>
    <button
      type="button"
      class="argon-snackbar__close"
      aria-label="Cerrar"
      @click="closeHandler"
    >
      <i class="fas fa-times" aria-hidden="true"></i>
    </button>
  </div>
</template>

<style scoped lang="scss">
.argon-snackbar {
  --snackbar-accent: #344767;
  --snackbar-accent-soft: rgba(52, 71, 103, 0.08);
  --snackbar-border: rgba(15, 23, 42, 0.08);
  --snackbar-title: #1f2937;
  --snackbar-text: #374151;
  --snackbar-surface: #ffffff;
  align-items: flex-start;
  background: var(--snackbar-surface);
  border: 1px solid var(--snackbar-border);
  border-left: 3px solid var(--snackbar-accent);
  border-radius: 0.75rem;
  box-shadow: 0 4px 20px rgba(15, 23, 42, 0.1);
  color: var(--snackbar-text);
  display: grid;
  gap: 0.625rem;
  grid-template-columns: auto minmax(0, 1fr) auto;
  margin-top: 0.5rem;
  max-width: 22rem;
  min-width: 18rem;
  overflow: hidden;
  padding: 0.875rem 0.75rem 0.875rem 0.875rem;
}

.argon-snackbar__accent {
  display: none;
}

.argon-snackbar__icon-wrap {
  color: var(--snackbar-accent);
  display: inline-flex;
  padding-top: 0.1rem;
}

.argon-snackbar__icon {
  font-size: 0.875rem;
}

.argon-snackbar__body {
  min-width: 0;
}

.argon-snackbar__topline {
  align-items: baseline;
  display: flex;
  gap: 0.5rem;
  justify-content: space-between;
  min-width: 0;
}

.argon-snackbar__title {
  color: var(--snackbar-title);
  font-size: 0.875rem;
  font-weight: 700;
  letter-spacing: 0.01em;
  line-height: 1.2;
}

.argon-snackbar__date {
  color: #9ca3af;
  flex-shrink: 0;
  font-size: 0.7rem;
  font-weight: 500;
}

.argon-snackbar__description {
  color: var(--snackbar-text);
  font-size: 0.8rem;
  line-height: 1.45;
  margin-top: 0.2rem;
}

.argon-snackbar__description :deep(ul) {
  margin: 0.2rem 0 0;
  padding-left: 1rem;
}

.argon-snackbar__description :deep(li + li) {
  margin-top: 0.15rem;
}

.argon-snackbar__close {
  align-items: center;
  align-self: flex-start;
  background: transparent;
  border: 0;
  border-radius: 0.375rem;
  color: #72808f;
  cursor: pointer;
  display: inline-flex;
  height: 1.5rem;
  justify-content: center;
  margin: -0.05rem -0.1rem 0 0;
  transition: background-color 0.15s ease, color 0.15s ease;
  width: 1.5rem;
}

.argon-snackbar__close:hover {
  background: rgba(15, 23, 42, 0.06);
  color: #374151;
}

.argon-snackbar.is-danger {
  --snackbar-accent: #e53e3e;
}

.argon-snackbar.is-success {
  --snackbar-accent: #2f855a;
}

@media (max-width: 576px) {
  .argon-snackbar {
    max-width: min(22rem, calc(100vw - 1.5rem));
    min-width: 0;
    width: calc(100vw - 1.5rem);
  }
}
</style>
